// FILE: /src/services/apps.js

import axiosServices from "./axios";

/**
 * Normalize backend errors so Apps UI can show consistent messages.
 */
const normalizeAppsError = (error, fallbackMessage = "Apps request failed.") => {
  if (error.response?.status === 403 && error.response?.data?.errorType === "LIMIT_REACHED") {
    const limitError = new Error(
      error.response.data.message ||
      error.response.data.error ||
      "You do not have enough credits for this action."
    );

    limitError.isLimitError = true;
    limitError.errorType = "LIMIT_REACHED";
    limitError.feature = error.response.data.feature || "Apps Credits";
    limitError.requiredCredits = error.response.data.requiredCredits;
    limitError.remainingCredits = error.response.data.remainingCredits;

    return limitError;
  }

  const normalError = new Error(
    error.response?.data?.message ||
      error.response?.data?.error ||
      error.message ||
      fallbackMessage
  );

  normalError.statusCode = error.response?.status;
  normalError.transactionId = error.response?.data?.transactionId;
  normalError.creditsRefunded = error.response?.data?.creditsRefunded;
  normalError.refundError = error.response?.data?.refundError;

  return normalError;
};

/**
 * GET /api/apps/catalog
 *
 * Returns:
 * {
 *   success: true,
 *   apps: [],
 *   credits: {
 *     totalCredits,
 *     consumedCredits,
 *     remainingCredits
 *   }
 * }
 */
export const fetchAppsCatalog = async () => {
  try {
    const response = await axiosServices.get("/api/apps/catalog");
    return response.data;
  } catch (error) {
    throw normalizeAppsError(error, "Failed to fetch apps catalog.");
  }
};

/**
 * GET /api/apps/history
 */
export const fetchAppsHistory = async ({
  groupId,
  appId,
  status,
  page = 1,
  limit = 30,
} = {}) => {
  try {
    const response = await axiosServices.get("/api/apps/history", {
      params: {
        groupId,
        appId,
        status,
        page,
        limit,
      },
    });

    return response.data;
  } catch (error) {
    throw normalizeAppsError(error, "Failed to fetch apps history.");
  }
};

/**
 * GET /api/apps/credits/estimate
 */
export const estimateAppCredits = async ({ appId, quantity = 1 }) => {
  try {
    const response = await axiosServices.get("/api/apps/credits/estimate", {
      params: {
        appId,
        quantity,
      },
    });

    return response.data?.estimate;
  } catch (error) {
    throw normalizeAppsError(error, "Failed to estimate app credits.");
  }
};

/**
 * GET /api/apps/transactions/:transactionId
 */
export const fetchAppTransaction = async (transactionId) => {
  try {
    const response = await axiosServices.get(`/api/apps/transactions/${transactionId}`);
    return response.data?.transaction;
  } catch (error) {
    throw normalizeAppsError(error, "Failed to fetch app transaction.");
  }
};

/**
 * POST /api/apps/background-remove
 *
 * Supports:
 * - File upload
 * - Existing image URL
 */
export const runBackgroundRemover = async ({ groupId, file = null, imageUrl = null }) => {
  try {
    if (!groupId) {
      throw new Error("groupId is required.");
    }

    if (!file && !imageUrl) {
      throw new Error("Please upload an image or select one from your media library.");
    }

    let response;

    if (file) {
      const formData = new FormData();
      formData.append("groupId", groupId);
      formData.append("image", file);

      response = await axiosServices.post("/api/apps/background-remove", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    } else {
      response = await axiosServices.post("/api/apps/background-remove", {
        groupId,
        imageUrl,
      });
    }

    return response.data;
  } catch (error) {
    if (error.response) {
      throw normalizeAppsError(error, "Background removal failed.");
    }

    throw error;
  }
};

export const runCaptionGenerator = async ({
  groupId,
  prompt = "",
  caption = "",
  files = [],
  urls = [],
  useBrandKit = true,
  options = {},
}) => {
  try {
    if (!groupId) {
      throw new Error("groupId is required.");
    }

    const hasFiles = Array.isArray(files) && files.length > 0;

    let response;

    if (hasFiles) {
      const formData = new FormData();

      formData.append("groupId", groupId);
      formData.append("prompt", prompt || "");
      formData.append("caption", caption || "");
      formData.append("useBrandKit", String(Boolean(useBrandKit)));
      formData.append("options", JSON.stringify(options || {}));

      if (Array.isArray(urls) && urls.length > 0) {
        formData.append("urls", JSON.stringify(urls));
      }

      files.forEach((file) => {
        formData.append("files", file);
      });

      response = await axiosServices.post("/api/apps/caption-generate", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    } else {
      response = await axiosServices.post("/api/apps/caption-generate", {
        groupId,
        prompt,
        caption,
        urls,
        useBrandKit,
        options,
      });
    }

    return response.data;
  } catch (error) {
    if (error.response) {
      throw normalizeAppsError(error, "Caption generation failed.");
    }

    throw error;
  }
};

export const runCaptionVariants = async ({
  groupId,
  caption,
  expand = false,
  generate_similar = false,
  n = 1,
  useBrandKit = true,
}) => {
  try {
    if (!groupId) {
      throw new Error("groupId is required.");
    }

    if (!caption) {
      throw new Error("caption is required.");
    }

    const response = await axiosServices.post("/api/apps/caption-variants", {
      groupId,
      caption,
      expand,
      generate_similar,
      n,
      useBrandKit,
    });

    return response.data;
  } catch (error) {
    if (error.response) {
      throw normalizeAppsError(error, "Caption variant generation failed.");
    }

    throw error;
  }
};

const appsService = {
  fetchAppsCatalog,
  fetchAppsHistory,
  estimateAppCredits,
  fetchAppTransaction,
  runBackgroundRemover,
  runCaptionGenerator,
  runCaptionVariants,
};

export default appsService;